import React, { useState } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const WeatherStationGame = ({ gameData, onComplete }) => {
    const [currentScenarioIndex, setCurrentScenarioIndex] = useState(0);
    const [selectedPrediction, setSelectedPrediction] = useState(null);
    const [score, setScore] = useState(0);
    const [showResult, setShowResult] = useState(false);

    const currentScenario = gameData?.scenarios?.[currentScenarioIndex];

    const handlePredictionSelect = (prediction) => {
        if (selectedPrediction) return;

        setSelectedPrediction(prediction);
        setShowResult(true);

        const isCorrect = prediction === currentScenario.correct_prediction;
        if (isCorrect) {
            setScore(prev => prev + (gameData.points_per_scenario || 25));
            triggerHaptic(100);
        } else {
            triggerHaptic([50, 50, 50]);
        }

        setTimeout(() => {
            if (currentScenarioIndex < gameData.scenarios.length - 1) {
                setCurrentScenarioIndex(prev => prev + 1);
                setSelectedPrediction(null);
                setShowResult(false);
            } else {
                onComplete?.(score + (isCorrect ? (gameData.points_per_scenario || 25) : 0));
            }
        }, 5000);
    };

    if (!gameData || !gameData.scenarios) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-rose-100">
                <h3 className="text-base md:text-3xl font-black text-rose-600 mb-4 tracking-tight">Klaida</h3>
                <p className="text-slate-500 font-bold text-lg">Nepavyko užkrauti žaidimo duomenų.</p>
            </div>
        );
    }

    const progress = (currentScenarioIndex / gameData.scenarios.length) * 100;
    const isCorrect = selectedPrediction === currentScenario?.correct_prediction;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-sky-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-sky-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-sky-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        🌤️
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Orų Stotis
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Taškai</p>
                        <p className="text-sm md:text-2xl font-black text-sky-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Analizė</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentScenarioIndex + 1}/{gameData.scenarios.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-sky-400 via-blue-500 to-indigo-600 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(56,189,248,0.3)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8">
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center text-center">
                <div className="absolute top-0 right-0 p-4 text-6xl opacity-[0.03] select-none pointer-events-none text-sky-900">☁️</div>
                <h4 className="text-[10px] font-black text-sky-400 uppercase tracking-[0.5em] mb-4 md:mb-10 text-center">Atmosferos Ženklai</h4>
                
                <div className="text-4xl md:text-8xl mb-4 md:mb-8 drop-shadow-2xl animate-pulse-subtle">{currentScenario?.signs_emoji}</div>
                <div className="bg-white/80 rounded-xl md:rounded-[3rem] p-6 md:p-12 shadow-inner border-2 border-sky-50 relative group w-full max-w-2xl">
                    <p className="text-base md:text-3xl font-black text-slate-800 leading-relaxed italic text-center font-serif">
                        "{currentScenario?.signs}"
                    </p>
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="space-y-4 md:space-y-6 flex flex-col items-center w-full max-w-4xl mx-auto">
            <div className="flex items-center gap-2 px-4 self-start">
                <div className="w-2 h-2 bg-sky-500 rounded-full animate-pulse"></div>
                <h4 className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-[0.4em]">Tavo Prognozė</h4>
            </div>
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-3 md:p-8 shadow-2xl border-white/60 bg-slate-900 relative overflow-hidden w-full min-h-[300px] flex flex-col justify-center">
                <div className="absolute top-0 right-0 p-4 text-6xl opacity-[0.05] select-none pointer-events-none text-white">📡</div>
                <div className="grid grid-cols-2 md:grid-cols-1 gap-3 md:gap-6 relative z-10 p-4 md:p-6">
                    {currentScenario?.prediction_options.map((option, index) => {
                        const isSelected = selectedPrediction === option;
                        const isCorrectAnswer = option === currentScenario.correct_prediction;
                        
                        let btnClass = 'premium-button-3d bg-white text-slate-700 border-slate-100 hover:scale-[1.02] border-b-8 md:border-b-[12px]';
                        if (showResult && isCorrectAnswer) btnClass = 'bg-emerald-500 text-white border-emerald-700 scale-105 shadow-emerald-500/30';
                        else if (showResult && isSelected && !isCorrectAnswer) btnClass = 'bg-rose-500 text-white border-rose-700 animate-shake';
                        else if (selectedPrediction && !showResult) btnClass = 'opacity-50 grayscale';

                        return (
                            <button
                                key={index}
                                onClick={() => handlePredictionSelect(option)}
                                disabled={!!selectedPrediction}
                                className={`
                                    flex flex-col items-center justify-center p-4 md:p-8 rounded-xl md:rounded-[2.5rem] text-sm md:text-xl font-black transition-all duration-300 gap-2 md:gap-4
                                    ${btnClass}
                                    disabled:cursor-default
                                `}
                            >
                                <span className="text-center leading-tight uppercase tracking-tighter">{option}</span>
                                {showResult && isSelected && (
                                    <div className="text-lg md:text-3xl animate-pop-in">
                                        {isCorrectAnswer ? '🎯' : '❌'}
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {showResult && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-8 md:p-16 rounded-[3rem] md:rounded-[4rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px]
                        ${isCorrect ? 'border-emerald-500' : 'border-sky-500'}
                    `}>
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-white rounded-full flex items-center justify-center text-4xl shadow-2xl border-4 border-inherit animate-bounce">
                            {isCorrect ? '✅' : '💡'}
                        </div>
                        
                        <div className="text-center space-y-4 pt-4">
                            <h2 className={`text-2xl md:text-5xl font-black uppercase tracking-tighter ${isCorrect ? 'text-emerald-600' : 'text-sky-600'}`}>
                                {isCorrect ? 'TIKSLU!' : 'MOKSLAS'}
                            </h2>

                            <div className={`p-4 md:p-8 rounded-2xl border-2 italic font-serif leading-relaxed ${isCorrect ? 'bg-emerald-50 border-emerald-100' : 'bg-sky-50 border-sky-100'}`}>
                                <p className="text-sm md:text-xl font-bold text-slate-700 text-center leading-relaxed">
                                    "{currentScenario.explanation}"
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default WeatherStationGame;
